import {
    CompressOutlined,
    ExpandAltOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { ChevronRight } from 'lucide-react';
import { SectionExtraButtons, SectionExtraTags } from './ExtraButtons';
import NodeOps from './NodeOps';
import {
    chapterNumbers,
    countOutlineResources,
    hasOwnNumbering,
    isLeafContentChapter,
} from './outlineUtils';
import styles from './index.module.scss';

const stop = (event) => event.stopPropagation();

function ChildResourceCount({ nodes }) {
    const { figures, tables } = countOutlineResources(nodes);
    if (!figures && !tables) return null;
    return (
        <span className={styles.sectionDesc} title="子级最末级小节中已选择的图表，不重复计入父级">
            子级含 {figures} 图 · {tables} 表
        </span>
    );
}

/** 展开 / 折叠指示：箭头 + 子级数量 */
function ExpandHint({ expanded, count, unit }) {
    return (
        <span className={styles.expandIndicator}>
            <ChevronRight
                size={13}
                aria-hidden
                className={classNames(styles.expandIcon, { [styles.expandIconOpen]: expanded })}
            />
            <span>
                {count}
                {unit}
            </span>
        </span>
    );
}

function SubsectionCard({ chapter, section, sub, number, ctx }) {
    const showNumber = number && !hasOwnNumbering(sub.chapter);
    return (
        <div className={styles.subsectionCard}>
            <div className={styles.sectionMainRow}>
                <div className={styles.sectionLeft}>
                    {showNumber && (
                        <span className={classNames(styles.sectionBadge, styles.subBadge)}>
                            {number}
                        </span>
                    )}
                    <div className={styles.sectionInfo}>
                        <div className={styles.sectionName}>{sub.chapter}</div>
                        {sub.chapterAbstract && (
                            <div className={styles.sectionDesc}>{sub.chapterAbstract}</div>
                        )}
                    </div>
                </div>
                <div className={styles.sectionRight}>
                    <SectionExtraButtons
                        sectionId={sub.id}
                        parentId={section.id}
                        existingExtras={sub.extras}
                        grandParentId={chapter.id}
                        sectionData={sub}
                        {...ctx.extraButtonProps}
                    />
                    <NodeOps
                        className={styles.sectionOperations}
                        onEdit={() => ctx.handleEditChapter(sub, true, section.id, 3, chapter.id)}
                        siblingLabel="添加同级子节"
                        onAddSibling={() =>
                            ctx.handleAddChapter(sub.id, true, section.id, 3, chapter.id)
                        }
                        onDelete={() =>
                            ctx.handleDeleteChapter(sub.id, true, section.id, 3, chapter.id)
                        }
                    />
                </div>
            </div>
            <SectionExtraTags extras={sub.extras} extraDesc={sub.extraDesc} sectionData={sub} />
        </div>
    );
}

function SectionCard({ chapter, section, number, ctx }) {
    const subs = section.subsections || [];
    const hasSubs = subs.length > 0;
    const expanded = ctx.expandedSections.has(section.id);
    const showNumber = number && !hasOwnNumbering(section.chapter);

    return (
        <div className={styles.sectionCard}>
            <div
                className={classNames(styles.sectionMainRow, { [styles.clickable]: hasSubs })}
                onClick={() => hasSubs && ctx.toggleSection(section.id)}
            >
                <div className={styles.sectionLeft}>
                    {showNumber && <span className={styles.sectionBadge}>{number}</span>}
                    <div className={styles.sectionInfo}>
                        <div className={styles.sectionName}>
                            {section.chapter}
                            {hasSubs && (
                                <ExpandHint expanded={expanded} count={subs.length} unit="小节" />
                            )}
                        </div>
                        {section.chapterAbstract && (
                            <div className={styles.sectionDesc}>{section.chapterAbstract}</div>
                        )}
                        <ChildResourceCount nodes={subs} />
                    </div>
                </div>

                <div className={styles.sectionRight} onClick={stop}>
                    <SectionExtraButtons
                        sectionId={section.id}
                        parentId={chapter.id}
                        existingExtras={section.extras}
                        grandParentId={null}
                        sectionData={section}
                        {...ctx.extraButtonProps}
                    />
                    <NodeOps
                        className={styles.sectionOperations}
                        onEdit={() => ctx.handleEditChapter(section, true, chapter.id)}
                        siblingLabel="添加同级节"
                        childLabel="添加子节"
                        onAddSibling={() => ctx.handleAddChapter(section.id, true, chapter.id)}
                        onAddChild={() =>
                            ctx.handleAddChapter(
                                hasSubs ? subs[subs.length - 1].id : null,
                                true,
                                section.id,
                                3,
                                chapter.id,
                            )
                        }
                        onDelete={() => ctx.handleDeleteChapter(section.id, true, chapter.id)}
                    />
                </div>
            </div>

            <SectionExtraTags
                extras={section.extras}
                extraDesc={section.extraDesc}
                sectionData={section}
            />

            {expanded && hasSubs && (
                <div className={styles.subsectionListWrapper}>
                    {subs.map((sub, subIdx) => (
                        <SubsectionCard
                            key={sub.id}
                            chapter={chapter}
                            section={section}
                            sub={sub}
                            number={number ? `${number}.${subIdx + 1}` : ''}
                            ctx={ctx}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ChapterCard({ chapter, chIdx, number, ctx }) {
    const sections = chapter.sections || [];
    const hasSections = sections.length > 0;
    const leaf = isLeafContentChapter(chapter);
    const expanded = ctx.expandedChapters.has(chapter.id);
    // 标题自带「第一章」时不再显示章号徽标；摘要 / 参考文献等固定章不占号
    const showNumber = number && !hasOwnNumbering(chapter.chapter);

    return (
        <div
            data-chapter-idx={chIdx}
            className={classNames(styles.outlineChapterCard, {
                [styles.streamingChapter]: ctx.isStreaming && chIdx === ctx.streamingChapterIdx,
            })}
        >
            <div
                className={classNames(styles.chapterRow, { [styles.clickable]: hasSections })}
                onClick={() => hasSections && ctx.toggleChapter(chapter.id)}
            >
                <div className={styles.chapterLeft}>
                    {showNumber && <div className={styles.chapterBadge}>第{number}章</div>}
                    <div className={styles.chapterInfo}>
                        <div className={styles.chapterName}>
                            {chapter.chapter}
                            {hasSections && (
                                <ExpandHint
                                    expanded={expanded}
                                    count={sections.length}
                                    unit="节"
                                />
                            )}
                        </div>
                        {chapter.chapterAbstract && (
                            <div className={styles.chapterDesc}>{chapter.chapterAbstract}</div>
                        )}
                        <ChildResourceCount nodes={sections} />
                    </div>
                </div>

                {chapter.chapter !== '参考文献' && (
                    <div className={styles.chapterOperations} onClick={stop}>
                        {/* 没有节的叶子章（开题报告 / 文献综述的模块）也能配图表：parentId 为空表示作用在章上 */}
                        {leaf && (
                            <SectionExtraButtons
                                sectionId={chapter.id}
                                parentId={null}
                                existingExtras={chapter.extras}
                                grandParentId={null}
                                sectionData={chapter}
                                {...ctx.extraButtonProps}
                            />
                        )}
                        <NodeOps
                            className={styles.chapterOps}
                            onEdit={() => ctx.handleEditChapter(chapter)}
                            siblingLabel="添加同级章节"
                            childLabel="添加子节"
                            onAddSibling={() => ctx.handleAddChapter(chapter.id)}
                            onAddChild={() =>
                                ctx.handleAddChapter(
                                    hasSections ? sections[sections.length - 1].id : null,
                                    true,
                                    chapter.id,
                                    2,
                                )
                            }
                            onDelete={() => ctx.handleDeleteChapter(chapter.id)}
                        />
                    </div>
                )}
            </div>

            {leaf && (
                <SectionExtraTags
                    className={styles.chapterExtraTags}
                    extras={chapter.extras}
                    extraDesc={chapter.extraDesc}
                    sectionData={chapter}
                />
            )}

            {expanded && hasSections && (
                <div className={styles.sectionListWrapper}>
                    {sections.map((section, secIdx) => (
                        <SectionCard
                            key={section.id}
                            chapter={chapter}
                            section={section}
                            number={number ? `${number}.${secIdx + 1}` : ''}
                            ctx={ctx}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

/** 普通引擎的大纲树（章 / 节 / 三级小节）。props 与 PremiumOutlineTree 一致 */
export default function OutlineTree({
    outlineData,
    isStreaming,
    streamingChapterIdx,
    expandedChapters,
    expandedSections,
    allExpanded,
    outlineContainerRef,
    generateOutline,
    regenerateDisabled,
    toggleChapter,
    toggleSection,
    toggleAllExpand,
    handleEditChapter,
    handleAddChapter,
    handleDeleteChapter,
    extraButtonProps,
}) {
    const chapters = Array.isArray(outlineData) ? outlineData : [];
    const numbers = chapterNumbers(chapters);
    const resourceCount = countOutlineResources(chapters);
    const ctx = {
        expandedChapters,
        expandedSections,
        isStreaming,
        streamingChapterIdx,
        toggleChapter,
        toggleSection,
        handleEditChapter,
        handleAddChapter,
        handleDeleteChapter,
        extraButtonProps,
    };

    return (
        <div className={styles.outlineContentWrapper}>
            <div className={styles.outlineToolbar}>
                <div className={styles.toolbarLeft}>
                    <span className={styles.toolbarIcon}>
                        <UnorderedListOutlined style={{ fontSize: 18 }} />
                    </span>
                    <span className={styles.toolbarTitle}>文章大纲</span>
                    <span className={styles.toolbarBadge}>共 {chapters.length} 章</span>
                    <span
                        className={styles.toolbarBadge}
                        title="按最末级小节的实际选择统计；同一小节选择多个图类型时分别计数"
                    >
                        {isStreaming ? '当前已规划' : '已规划'} {resourceCount.figures} 图 ·{' '}
                        {resourceCount.tables} 表
                    </span>
                </div>
                <div className={styles.toolbarRight}>
                    <button
                        type="button"
                        className={styles.toolbarBtn}
                        disabled={regenerateDisabled}
                        onClick={generateOutline}
                    >
                        <ReloadOutlined /> 重新生成
                    </button>
                    <button type="button" className={styles.toolbarBtn} onClick={toggleAllExpand}>
                        {allExpanded ? <CompressOutlined /> : <ExpandAltOutlined />}
                        {allExpanded ? '折叠' : '展开'}
                    </button>
                </div>
            </div>

            <div className={styles.outlineListContainer} ref={outlineContainerRef}>
                {chapters.map((chapter, chIdx) => (
                    <ChapterCard
                        key={chapter.id}
                        chapter={chapter}
                        chIdx={chIdx}
                        number={numbers[chIdx]}
                        ctx={ctx}
                    />
                ))}
            </div>
        </div>
    );
}
